<template>
  <teleport to="body">
    <div
      class="toast-container position-fixed top-0 end-0 p-3"
      style="z-index: 9999"
    >
      <transition-group name="toast" tag="div">
        <div
          v-for="notification in notifications"
          :key="notification.id"
          class="toast show mb-2"
          :class="getToastClass(notification.type)"
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
        >
          <div class="toast-header" :class="getHeaderClass(notification.type)">
            <i :class="getIcon(notification.type)" class="me-2"></i>
            <strong class="me-auto">{{ notification.title }}</strong>
            <small class="text-muted">{{
              getTimeAgo(notification.timestamp)
            }}</small>
            <button
              type="button"
              class="btn-close btn-close-white"
              @click="removeNotification(notification.id)"
              aria-label="Cerrar"
            ></button>
          </div>
          <div class="toast-body text-white">
            <div class="toast-message">{{ notification.message }}</div>
            <div
              v-if="
                notification.contactSupport || isSupportNeeded(notification)
              "
              class="toast-actions mt-2 pt-2 border-top border-white border-opacity-25 d-flex gap-2 flex-wrap align-items-center"
            >
              <a
                :href="getWhatsAppSupportUrl(notification)"
                target="_blank"
                rel="noopener noreferrer"
                class="btn btn-sm btn-light text-dark fw-bold d-inline-flex align-items-center shadow-sm py-1 px-2 text-decoration-none rounded-pill"
                style="font-size: 0.78rem"
              >
                <i class="bi bi-whatsapp text-success me-1 fs-6"></i>
                Contactar Soporte Técnico
              </a>
              <button
                type="button"
                class="btn btn-sm btn-outline-light py-1 px-2 d-inline-flex align-items-center rounded-pill"
                style="font-size: 0.75rem"
                @click="copyError(notification)"
              >
                <i
                  class="bi me-1"
                  :class="
                    copiedId === notification.id ? 'bi-check2' : 'bi-clipboard'
                  "
                ></i>
                {{
                  copiedId === notification.id ? "¡Copiado!" : "Copiar detalle"
                }}
              </button>
            </div>
          </div>
        </div>
      </transition-group>
    </div>
  </teleport>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

export default {
  name: "ToastNotification",

  data() {
    return {
      copiedId: null,
    };
  },

  computed: {
    ...mapGetters("notificationStore", ["notifications"]),
  },

  watch: {
    notifications: {
      handler(newNotifications) {
        // Auto-eliminar notificaciones después de su duración
        newNotifications.forEach((notification) => {
          if (notification.duration > 0 && !notification.timeoutId) {
            notification.timeoutId = setTimeout(() => {
              this.removeNotification(notification.id);
            }, notification.duration);
          }
        });
      },
      deep: true,
      immediate: true,
    },
  },

  methods: {
    ...mapActions("notificationStore", ["remove"]),

    removeNotification(id) {
      this.remove(id);
    },

    getToastClass(type) {
      const classes = {
        success: "bg-success",
        danger: "bg-danger",
        warning: "bg-warning",
        info: "bg-info",
      };
      return classes[type] || "bg-secondary";
    },

    getHeaderClass(type) {
      const classes = {
        success: "bg-success text-white",
        danger: "bg-danger text-white",
        warning: "bg-warning text-dark",
        info: "bg-info text-white",
      };
      return classes[type] || "bg-secondary text-white";
    },

    getIcon(type) {
      const icons = {
        success: "bi bi-check-circle-fill",
        danger: "bi bi-x-circle-fill",
        warning: "bi bi-exclamation-triangle-fill",
        info: "bi bi-info-circle-fill",
      };
      return icons[type] || "bi bi-bell-fill";
    },

    getTimeAgo(timestamp) {
      const seconds = Math.floor((Date.now() - timestamp) / 1000);
      if (seconds < 10) return "Ahora";
      if (seconds < 60) return `${seconds}s`;
      const minutes = Math.floor(seconds / 60);
      return `${minutes}m`;
    },

    isSupportNeeded(notification) {
      if (notification.type !== "danger") return false;
      const text = `${notification.title || ""} ${
        notification.message || ""
      }`.toLowerCase();
      return (
        text.includes("servidor") ||
        text.includes("sistema") ||
        text.includes("soporte") ||
        text.includes("procesamiento") ||
        text.includes("técnico") ||
        text.includes("código")
      );
    },

    getWhatsAppSupportUrl(notification) {
      const errorText = encodeURIComponent(
        `Hola Soporte Vaquiano! Se produjo un error en la aplicación:\n\n*Título:* ${
          notification.title || "Error"
        }\n*Detalle:* ${notification.message || ""}\n*Fecha/Hora:* ${new Date(
          notification.timestamp || Date.now()
        ).toLocaleString()}`
      );
      return `https://wa.me/5493576657682?text=${errorText}`;
    },

    async copyError(notification) {
      const fullText = `[Error Vaquiano] ${notification.title || "Error"}: ${
        notification.message || ""
      }`;
      try {
        await navigator.clipboard.writeText(fullText);
        this.copiedId = notification.id;
        setTimeout(() => {
          if (this.copiedId === notification.id) {
            this.copiedId = null;
          }
        }, 2500);
      } catch (err) {
        console.warn("No se pudo copiar al portapapeles:", err);
      }
    },
  },
};
</script>

<style scoped>
.toast-container {
  max-width: 400px;
  width: 100%;
  top: calc(env(safe-area-inset-top, 0px) + 0.75rem) !important;
  z-index: 9999;
}

.toast {
  opacity: 1;
  border: none;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  min-width: 300px;
}

.toast-header {
  border-bottom: none;
}

.toast-body {
  word-wrap: break-word;
}

/* Animaciones */
.toast-enter-active {
  animation: slideInRight 0.3s ease-out;
}

.toast-leave-active {
  animation: slideOutRight 0.3s ease-in;
}

@keyframes slideInRight {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes slideOutRight {
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
}

/* Responsive */
@media (max-width: 576px) {
  .toast-container {
    left: 0;
    right: 0;
    max-width: 100%;
    padding: 0.5rem !important;
  }

  .toast {
    min-width: auto;
    width: 100%;
  }
}
</style>
